import React, { useState, useEffect } from 'react';
import { posts as initialPosts, categories as initialCategories } from '../../api/mockData';
import './index.css';

function Archive() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);

  // 加载所有文章数据
  useEffect(() => {
    loadPosts();
    
    // 监听文章更新事件
    const handlePostsUpdated = () => {
      loadPosts();
    };

    window.addEventListener('blogPostsUpdated', handlePostsUpdated);

    return () => {
      window.removeEventListener('blogPostsUpdated', handlePostsUpdated);
    };
  }, []);

  const loadPosts = () => {
    try {
      // 1. 从localStorage加载用户文章
      const savedPosts = JSON.parse(localStorage.getItem('blogPosts')) || [];
      
      // 2. 合并初始文章和用户文章
      const mergedPosts = [...savedPosts, ...initialPosts];
      
      // 3. 去重（按ID）
      const uniquePosts = mergedPosts.reduce((acc, post) => {
        if (!acc.some(p => p.id === post.id)) {
          acc.push(post);
        }
        return acc;
      }, []);
      
      // 4. 提取所有唯一的分类
      const allCats = Array.from(
        new Set([
          ...initialCategories,
          ...uniquePosts.map(post => post.category).filter(Boolean)
        ])
      ).filter(cat => cat && cat !== '未分类');
      
      setAllPosts(uniquePosts);
      setAllCategories(allCats);
    } catch (error) {
      console.error('加载文章失败:', error);
      setAllPosts(initialPosts);
      setAllCategories(initialCategories);
    }
  };

  // 处理归档数据
  const archiveData = allPosts.reduce((acc, post) => {
    const year = post.date ? post.date.split('-')[0] : '未分类';
    const month = post.date 
      ? new Date(post.date).toLocaleString('zh-CN', { month: 'long' })
      : '未知';
    
    if (!acc[year]) {
      acc[year] = {};
    }
    if (!acc[year][month]) {
      acc[year][month] = [];
    }
    
    acc[year][month].push(post);
    return acc;
  }, {});

  // 获取所有标签（去重）
  const allTags = [...new Set(allPosts.flatMap(post => post.tags || []))];

  // 获取年份统计
  const yearStats = Object.keys(archiveData).reduce((stats, year) => {
    const totalPosts = Object.values(archiveData[year]).flat().length;
    stats[year] = totalPosts;
    return stats;
  }, {});

  // 获取分类统计
  const categoryStats = allCategories.reduce((stats, category) => {
    const count = allPosts.filter(post => post.category === category).length;
    stats[category] = count;
    return stats;
  }, {});

  // 获取标签统计
  const tagStats = allTags.reduce((stats, tag) => {
    const count = allPosts.filter(post => (post.tags || []).includes(tag)).length;
    stats[tag] = count;
    return stats;
  }, {});

  // 过滤文章函数
  const getFilteredArchiveData = () => {
    if (!activeCategory && !activeTag) return archiveData;

    const filtered = {};
    Object.entries(archiveData).forEach(([year, months]) => {
      Object.entries(months).forEach(([month, monthPosts]) => {
        const filteredPosts = monthPosts.filter(post => {
          if (activeCategory && post.category !== activeCategory) return false;
          if (activeTag && !(post.tags || []).includes(activeTag)) return false;
          return true;
        });

        if (filteredPosts.length > 0) {
          if (!filtered[year]) filtered[year] = {};
          filtered[year][month] = filteredPosts;
        }
      });
    });

    return filtered;
  };

  const filteredArchiveData = getFilteredArchiveData();

  // 中文月份映射
  const monthMap = {
    'January': '一月',
    'February': '二月',
    'March': '三月',
    'April': '四月',
    'May': '五月',
    'June': '六月',
    'July': '七月',
    'August': '八月',
    'September': '九月',
    'October': '十月',
    'November': '十一月',
    'December': '十二月'
  };

  return (
    <div className="archive-container">
      {/* Hero Header */}
      <div className="archive-hero">
        <div className="container">
          <h1 className="archive-title">文章归档</h1>
          <p className="archive-subtitle">探索所有文章，按时间、分类和标签浏览</p>
          <div className="archive-stats">
            <div className="stat-item">
              <span className="stat-number">{allPosts.length}</span>
              <span className="stat-label">篇文章</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{allCategories.length}</span>
              <span className="stat-label">个分类</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{allTags.length}</span>
              <span className="stat-label">个标签</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{Object.keys(archiveData).length}</span>
              <span className="stat-label">个年份</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="archive-content">
          {/* Sidebar */}
          <div className="archive-sidebar">
            {/* Categories Widget */}
            <div className="sidebar-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">📁</span>
                  分类
                </h3>
                <span className="widget-count">{allCategories.length}</span>
              </div>
              <div className="widget-content">
                <ul className="category-list">
                  <li className={`category-item ${!activeCategory ? 'active' : ''}`}>
                    <button 
                      onClick={() => setActiveCategory(null)}
                      className="category-link"
                    >
                      <span className="category-name">全部分类</span>
                      <span className="category-count">{allPosts.length}</span>
                    </button>
                  </li>
                  {allCategories.map(category => (
                    <li key={category} className={`category-item ${activeCategory === category ? 'active' : ''}`}>
                      <button 
                        onClick={() => setActiveCategory(category)}
                        className="category-link"
                      >
                        <span className="category-name">{category}</span>
                        <span className="category-count">{categoryStats[category] || 0}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Tags Widget */}
            <div className="sidebar-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">🏷️</span>
                  热门标签
                </h3>
              </div>
              <div className="widget-content">
                <div className="tags-cloud">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      className={`tag ${activeTag === tag ? 'active' : ''}`}
                      onClick={() => setActiveTag(tag === activeTag ? null : tag)}
                    >
                      #{tag}
                      <span className="tag-count">{tagStats[tag] || 0}</span>
                    </button>
                  ))}
                </div>
                <div className="tags-actions">
                  <button 
                    className="clear-tags-btn"
                    onClick={() => {
                      setActiveTag(null);
                      setActiveCategory(null);
                    }}
                  >
                    <span className="clear-icon">↻</span>
                    清除筛选
                  </button>
                </div>
              </div>
            </div>

            {/* Year Stats Widget */}
            <div className="sidebar-widget">
              <div className="widget-header">
                <h3 className="widget-title">
                  <span className="widget-icon">📅</span>
                  年度统计
                </h3>
              </div>
              <div className="widget-content">
                <ul className="year-stats">
                  {Object.entries(yearStats)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([year, count]) => (
                    <li key={year} className="year-stat">
                      <div className="year-info">
                        <span className="year">{year}</span>
                        <span className="year-count">{count} 篇文章</span>
                      </div>
                      <div className="year-bar">
                        <div 
                          className="year-progress"
                          style={{ 
                            width: `${(count / Math.max(...Object.values(yearStats))) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="archive-main">
            {/* Filter Status */}
            {(activeCategory || activeTag) && (
              <div className="filter-status">
                <span className="filter-label">当前筛选：</span>
                {activeCategory && (
                  <span className="filter-badge">
                    <span className="filter-icon">📁</span>
                    {activeCategory}
                    <button 
                      onClick={() => setActiveCategory(null)}
                      className="remove-filter"
                    >
                      ×
                    </button>
                  </span>
                )}
                {activeTag && (
                  <span className="filter-badge">
                    <span className="filter-icon">🏷️</span>
                    #{activeTag}
                    <button 
                      onClick={() => setActiveTag(null)}
                      className="remove-filter"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Timeline */}
            <div className="timeline-section">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">📅</span>
                  时间轴
                </h2>
                <div className="section-subtitle">按年份和月份查看文章</div>
              </div>

              {Object.keys(filteredArchiveData).length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>未找到匹配的文章</h3>
                  <p>尝试选择其他分类或标签</p>
                </div>
              ) : (
                <div className="timeline">
                  {Object.entries(filteredArchiveData)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([year, months]) => (
                    <div key={year} className="timeline-year">
                      <div className="year-header">
                        <div className="year-label">
                          <span className="year-number">{year}</span>
                          <span className="year-post-count">
                            {Object.values(months).flat().length} 篇文章
                          </span>
                        </div>
                        <div className="year-line"></div>
                      </div>
                      
                      <div className="timeline-months">
                        {Object.entries(months)
                          .sort(([a], [b]) => {
                            const monthOrder = Object.keys(monthMap);
                            return monthOrder.indexOf(b) - monthOrder.indexOf(a);
                          })
                          .map(([month, monthPosts]) => (
                          <div key={month} className="timeline-month">
                            <div className="month-header">
                              <h4 className="month-name">
                                {monthMap[month] || month}
                              </h4>
                              <span className="month-count">{monthPosts.length} 篇</span>
                            </div>
                            <div className="month-posts">
                              {monthPosts
                                .sort((a, b) => {
                                  if (!a.date || !b.date) return 0;
                                  return new Date(b.date) - new Date(a.date);
                                })
                                .map(post => (
                                <div key={post.id} className="post-card">
                                  <div className="post-date-badge">
                                    <span className="post-day">
                                      {post.date ? post.date.split('-')[2] : '?'}
                                    </span>
                                    <span className="post-month">
                                      {month.slice(0, 3)}
                                    </span>
                                  </div>
                                  <div className="post-content">
                                    <h5 className="post-title">
                                      <a href={`/post/${post.id}`} className="post-link">
                                        {post.title || '无标题'}
                                      </a>
                                    </h5>
                                    <p className="post-excerpt">
                                      {post.summary || (post.content ? post.content.substring(0, 100) + '...' : '暂无摘要')}
                                    </p>
                                    <div className="post-meta">
                                      <span className="post-readtime">
                                        ⏱️ {post.readTime || '5分钟'}
                                      </span>
                                      <div className="post-tags">
                                        {(post.tags || []).slice(0, 3).map(tag => (
                                          <span key={tag} className="post-tag">
                                            #{tag}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Categories Grid */}
            <div className="categories-section">
              <div className="section-header">
                <h2 className="section-title">
                  <span className="section-icon">📂</span>
                  所有分类
                </h2>
              </div>
              <div className="categories-grid">
                {allCategories.map(category => (
                  <div key={category} className="category-card">
                    <div className="category-icon">📁</div>
                    <h3 className="category-name">{category}</h3>
                    <p className="category-description">探索所有关于{category}的文章</p>
                    <div className="category-meta">
                      <span className="category-count">
                        {categoryStats[category] || 0} 篇文章
                      </span>
                      <button 
                        onClick={() => setActiveCategory(category)}
                        className="category-button"
                      >
                        查看
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Archive;